import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type MfaEnrollment, type MfaStatus, type RetentionPolicy } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { useRetention } from '../lib/queries'

export function Security() {
  const qc = useQueryClient()
  const status = useQuery({ queryKey: ['mfa'], queryFn: () => api<MfaStatus>('/v1/auth/mfa') })
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null)
  const [code, setCode] = useState('')
  const [done, setDone] = useState(false)

  const start = useMutation({
    mutationFn: () => apiWrite<MfaEnrollment>('/v1/auth/mfa/enroll', 'POST', {}),
    onSuccess: (e) => { setEnrollment(e); setDone(false) },
  })
  const confirm = useMutation({
    mutationFn: () => apiWrite('/v1/auth/mfa/confirm', 'POST', { code }),
    onSuccess: () => { setDone(true); setEnrollment(null); setCode(''); void qc.invalidateQueries({ queryKey: ['mfa'] }) },
  })

  const enabled = status.data?.enabled
  const retention = useRetention()
  const [days, setDays] = useState<number | ''>('')
  const [auditDays, setAuditDays] = useState<number | ''>('')
  const [saved, setSaved] = useState<string | null>(null)
  const saveRetention = useMutation({
    mutationFn: () => apiWrite<RetentionPolicy>('/v1/retention', 'PUT', {
      documents_days: Number(days || retention.data?.documents_days),
      audit_days: Number(auditDays || retention.data?.audit_days),
      auto_purge: retention.data?.auto_purge ?? false,
    }),
    onSuccess: (p) => { setSaved(`บันทึกแล้ว · เอกสาร ${p.documents_days} วัน · audit ${p.audit_days} วัน`); void qc.invalidateQueries({ queryKey: ['retention'] }) },
    onError: (e) => setSaved(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
  })

  return (
    <>
      <PageHead title="ความปลอดภัย" sub="ยืนยันตัวตนสองขั้นตอนด้วยแอป Authenticator" />

      {status.isLoading ? <Skel h={180} /> : (
        <div className="grid-main">
          <div className="card card-pad">
            <div className="row">
              <span className="card-label">การยืนยันสองขั้นตอน (TOTP)</span>
              <span className={`chip${enabled ? ' gold' : ''}`} style={{ marginLeft: 'auto' }}>
                {enabled ? 'เปิดใช้งาน' : 'ยังไม่เปิด'}
              </span>
            </div>

            <p className="dim" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.7 }}>
              เมื่อเปิดแล้ว การเข้าสู่ระบบจะถามรหัส 6 หลักจากแอป Google Authenticator,
              1Password หรือ Microsoft Authenticator หลังกรอกรหัสผ่าน
            </p>

            {!enabled && !enrollment ? (
              <button className="btn btn-primary" style={{ marginTop: 18 }} disabled={start.isPending} onClick={() => start.mutate()}>
                {start.isPending ? 'กำลังสร้างคีย์…' : 'เปิดใช้งาน'}
              </button>
            ) : null}

            {enrollment ? (
              <div style={{ marginTop: 20 }}>
                <div className="eyebrow">เพิ่มบัญชีในแอป Authenticator ด้วยคีย์นี้</div>
                <div className="mono" style={{ fontSize: 15, letterSpacing: '.08em', marginTop: 8, padding: '12px 14px',
                  border: '1px solid var(--color-line-strong)', borderRadius: 6, wordBreak: 'break-all' }}>
                  {enrollment.secret}
                </div>
                <a className="dim" href={enrollment.otpauth_url} style={{ fontSize: 11.5, display: 'inline-block', marginTop: 8 }}>
                  เปิดในแอปผ่าน otpauth
                </a>
                <div className="field" style={{ marginTop: 18 }}>
                  <label htmlFor="otp">รหัส 6 หลักจากแอป</label>
                  <input id="otp" className="input mono" inputMode="numeric" maxLength={6} autoFocus
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    style={{ letterSpacing: '.4em', fontSize: 18, maxWidth: 220 }} />
                </div>
                {confirm.isError ? <div className="form-error">รหัสไม่ถูกต้อง — ตรวจเวลาบนอุปกรณ์แล้วลองใหม่</div> : null}
                <button className="btn btn-primary" style={{ marginTop: 14 }}
                  disabled={code.length !== 6 || confirm.isPending} onClick={() => confirm.mutate()}>
                  {confirm.isPending ? 'กำลังตรวจสอบ…' : 'ยืนยันและเปิดใช้งาน'}
                </button>
              </div>
            ) : null}

            {done ? <div style={{ marginTop: 16, fontSize: 12.5, color: 'var(--color-emerald-soft)' }}>เปิดใช้งานเรียบร้อย — ครั้งถัดไปที่เข้าสู่ระบบจะถามรหัสจากแอป</div> : null}
          </div>

          <div className="card card-pad">
            <div className="card-label">สิ่งที่ระบบทำเมื่อเปิด MFA</div>
            <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
              <li>เก็บ secret แบบเข้ารหัส AES-GCM — อ่านจากฐานข้อมูลตรง ๆ ไม่ได้</li>
              <li>ออก token ชั่วคราว 5 นาทีสำหรับขั้นที่สอง แยกจาก access token</li>
              <li>token ขั้นที่สองเรียก API อื่นไม่ได้ (ทดสอบแล้วได้ 401)</li>
              <li>บันทึกเหตุการณ์ mfa.enabled / auth.mfa_login ลง audit trail</li>
            </ul>
          </div>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="row">
          <span className="card-label">การเก็บรักษาข้อมูล (PDPA)</span>
          <span className="chip" style={{ marginLeft: 'auto' }}>
            {retention.data ? `${retention.data.documents_days.toLocaleString('th-TH')} วัน` : '—'}
          </span>
        </div>
        <p className="dim" style={{ fontSize: 12.5, marginTop: 12, lineHeight: 1.7 }}>
          เอกสารเก่ากว่ากำหนดจะถูกลบออกจากระบบ (ยกเว้นที่อายัดทางกฎหมาย) ส่วนร่องรอยการตรวจสอบเก็บนานกว่าเสมอ
        </p>
        <div className="row" style={{ marginTop: 14, gap: 12, flexWrap: 'wrap' }}>
          <div className="field" style={{ maxWidth: 180 }}>
            <label htmlFor="docdays">เก็บเอกสาร (วัน)</label>
            <input id="docdays" className="input tnum" type="number" min={30} max={7300}
              placeholder={String(retention.data?.documents_days ?? 2555)}
              value={days} onChange={(e) => setDays(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <div className="field" style={{ maxWidth: 180 }}>
            <label htmlFor="auditdays">เก็บ audit trail (วัน)</label>
            <input id="auditdays" className="input tnum" type="number" min={365} max={7300}
              placeholder={String(retention.data?.audit_days ?? 3650)}
              value={auditDays} onChange={(e) => setAuditDays(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <button className="btn btn-primary" style={{ alignSelf: 'flex-end', height: 38 }}
            disabled={saveRetention.isPending} onClick={() => saveRetention.mutate()}>
            {saveRetention.isPending ? 'กำลังบันทึก…' : 'บันทึกนโยบาย'}
          </button>
        </div>
        {saved ? <div className="dim" style={{ marginTop: 10, fontSize: 12 }}>{saved}</div> : null}
      </div>
    </>
  )
}
